/**
 * API envelope and typed fetch.
 *
 * The server answers with `{ ok, data, error, requestId }` for every call. Nothing else
 * is allowed to throw an untyped error at the UI: an operator at a till needs a message they
 * can act on, and `requestId` is what support will ask for, so both travel on the error.
 */

export interface ApiErrorBody {
  code: string;
  message: string;
  details?: unknown;
  requestId?: string;
}

/** Matches apps/api/src/http.ts: `{ success, data }` or `{ success: false, error }`. */
export interface Envelope<T> {
  success: boolean;
  data?: T;
  error?: ApiErrorBody;
}

export class ApiError extends Error {
  readonly status: number;
  readonly code: string;
  readonly requestId: string | undefined;
  readonly details: unknown;

  constructor(status: number, body: ApiErrorBody, requestId: string | undefined) {
    super(body.message);
    this.name = 'ApiError';
    this.status = status;
    this.code = body.code;
    this.requestId = requestId;
    this.details = body.details;
  }

  /** True when re-authenticating is the only way forward, so the shell can bounce to login. */
  get isAuthFailure(): boolean {
    return this.status === 401;
  }
}

/** True when the request never reached the server: offline, DNS failure, tunnel dropped. */
export function isNetworkError(error: unknown): boolean {
  return error instanceof TypeError && /fetch|network|load failed/i.test(error.message);
}

const TOKEN_KEY = 'sp.accessToken';
const REFRESH_KEY = 'sp.refreshToken';

export function readAccessToken(): string | null {
  return localStorage.getItem(TOKEN_KEY);
}

export function writeTokens(accessToken: string, refreshToken: string): void {
  localStorage.setItem(TOKEN_KEY, accessToken);
  localStorage.setItem(REFRESH_KEY, refreshToken);
}

export function clearTokens(): void {
  localStorage.removeItem(TOKEN_KEY);
  localStorage.removeItem(REFRESH_KEY);
}

type RefreshHandler = () => Promise<boolean>;

let refreshHandler: RefreshHandler | null = null;

/** Registered by the auth store so the client can refresh silently and retry once. */
export function setRefreshHandler(handler: RefreshHandler | null): void {
  refreshHandler = handler;
}

let refreshInFlight: Promise<boolean> | null = null;

async function refreshOnce(): Promise<boolean> {
  // Collapse a burst of parallel 401s into a single refresh: three requests firing three
  // refreshes would rotate the token three times and log the cashier out.
  refreshInFlight ??= (async () => {
    try {
      return (await refreshHandler?.()) ?? false;
    } catch {
      return false;
    } finally {
      refreshInFlight = null;
    }
  })();
  return refreshInFlight;
}

/**
 * Where the API lives. Empty (the default) keeps requests relative (`/api/...`), which the dev
 * proxy and any same-origin reverse proxy serve. Set VITE_API_BASE at build time to point a
 * statically hosted web app at a separate API origin; a trailing slash is tolerated.
 */
export const API_BASE: string = (import.meta.env.VITE_API_BASE ?? '').trim().replace(/\/+$/, '');
const BASE = API_BASE;

export interface RequestOptions {
  method?: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';
  body?: unknown;
  /** Raw responses (CSV downloads, receipt files) skip envelope unwrapping. */
  raw?: boolean;
  signal?: AbortSignal;
  query?: Record<string, string | number | boolean | null | undefined>;
}

function buildUrl(path: string, query?: RequestOptions['query']): string {
  const url = `${BASE}${path}`;
  if (!query) return url;
  const params = new URLSearchParams();
  for (const [key, value] of Object.entries(query)) {
    if (value !== undefined && value !== null && value !== '') params.set(key, String(value));
  }
  const search = params.toString();
  return search ? `${url}?${search}` : url;
}

async function send(path: string, options: RequestOptions, _isRetry: boolean): Promise<Response> {
  const headers: Record<string, string> = {};
  const token = readAccessToken();
  if (token) headers.authorization = `Bearer ${token}`;
  if (options.body !== undefined) headers['content-type'] = 'application/json';

  return fetch(buildUrl(path, options.query), {
    method: options.method ?? 'GET',
    headers,
    body: options.body === undefined ? undefined : JSON.stringify(options.body),
    signal: options.signal ?? null,
  });
}

export async function apiRequest<T>(path: string, options: RequestOptions = {}): Promise<T> {
  let response = await send(path, options, false);

  if (response.status === 401 && !options.raw && readAccessToken() && !options.query?.refresh) {
    if (await refreshOnce()) {
      response = await send(path, options, true);
    }
  }

  if (options.raw) {
    if (!response.ok) {
      throw new ApiError(response.status, await safeError(response), response.headers.get('x-request-id') ?? undefined);
    }
    return response as unknown as T;
  }

  let envelope: Envelope<T>;
  try {
    envelope = (await response.json()) as Envelope<T>;
  } catch {
    throw new ApiError(
      response.status,
      { code: 'BAD_RESPONSE', message: `The server sent a response we could not read (HTTP ${response.status}).` },
      response.headers.get('x-request-id') ?? undefined,
    );
  }

  if (!response.ok || envelope.success !== true || envelope.data === undefined) {
    throw new ApiError(
      response.status,
      envelope.error ?? { code: 'UNKNOWN', message: `Request failed (HTTP ${response.status}).` },
      envelope.error?.requestId ?? response.headers.get('x-request-id') ?? undefined,
    );
  }

  return envelope.data;
}

async function safeError(response: Response): Promise<ApiErrorBody> {
  try {
    const envelope = (await response.json()) as Envelope<unknown>;
    return envelope.error ?? { code: 'UNKNOWN', message: `Request failed (HTTP ${response.status}).` };
  } catch {
    return { code: 'UNKNOWN', message: `Request failed (HTTP ${response.status}).` };
  }
}

export const api = {
  get: <T>(path: string, query?: RequestOptions['query'], signal?: AbortSignal) =>
    apiRequest<T>(path, { method: 'GET', ...(query ? { query } : {}), ...(signal ? { signal } : {}) }),
  post: <T>(path: string, body?: unknown, signal?: AbortSignal) => apiRequest<T>(path, { method: 'POST', body, signal }),
  put: <T>(path: string, body?: unknown) => apiRequest<T>(path, { method: 'PUT', body }),
  patch: <T>(path: string, body?: unknown) => apiRequest<T>(path, { method: 'PATCH', body }),
  delete: <T>(path: string, body?: unknown) => apiRequest<T>(path, { method: 'DELETE', body }),
  raw: <T>(path: string, query?: RequestOptions['query']) => apiRequest<T>(path, { method: 'GET', raw: true, ...(query ? { query } : {}) }),
};